"use client"

import React from "react"
import { createContext, useState, useContext, useEffect, type ReactNode } from "react"
import { authAPI } from "../services/api"

interface User {
  id: string
  username: string
  fullName?: string
  email?: string
  phone?: string
  bio?: string
  profileImage?: string
  role: string
}

interface AuthContextType {
  user: User | null
  token: string | null
  loading: boolean
  isLoading: boolean
  error: string | null
  login: (email: string, password: string) => Promise<void>
  logout: () => Promise<void>
  isAuthenticated: boolean
}

const AuthContext = createContext<AuthContextType | undefined>(undefined)

export const AuthProvider = ({ children }: { children: ReactNode }) => {
  const [user, setUser] = useState<User | null>(null)
  const [token, setToken] = useState<string | null>(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)

  const isAuthenticated = !!user && !!token

  useEffect(() => {
    loadStoredAuth()
  }, [])

  const loadStoredAuth = async () => {
    try {
      const storedToken = localStorage.getItem("admin_token")
      const storedUser = localStorage.getItem("admin_user")

      if (storedToken && storedUser) {
        setToken(storedToken)
        setUser(JSON.parse(storedUser))
      }
    } catch (err) {
      console.error("Error loading stored auth:", err)
    } finally {
      setLoading(false)
    }
  }

  const login = async (email: string, password: string) => {
    try {
      setError(null)
      setLoading(true)

      const response = await authAPI.adminLogin(email, password)
      const { token: newToken, user: userData } = response.data.data || response.data

      if (!newToken || !userData) {
        throw new Error("Authentication failed: missing token or user")
      }

      localStorage.setItem("admin_token", newToken)
      localStorage.setItem("admin_user", JSON.stringify(userData))

      setToken(newToken)
      setUser(userData)
    } catch (err: any) {
      setError(err.message || "Login failed")
      throw err
    } finally {
      setLoading(false)
    }
  }

  const logout = async () => {
    try {
      localStorage.removeItem("admin_token")
      localStorage.removeItem("admin_user")
      setUser(null)
      setToken(null)
    } catch (err) {
      console.error("Error logging out:", err)
    }
  }

  const value: AuthContextType = {
    user,
    token,
    loading,
    isLoading: loading, // Alias for compatibility
    error,
    login,
    logout,
    isAuthenticated,
  }

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>
}

export const useAuth = () => {
  const context = useContext(AuthContext)
  if (!context) {
    throw new Error("useAuth must be used within an AuthProvider")
  }
  return context
}
